import { useCallback, useMemo, useState } from 'react'
import { Grid } from '@mui/material'
import { makeStyles } from '@mui/styles'

import LeaveConfirmationDialog from '~/components/common/dialog/LeaveConfirmationDialog'
import ChewyPrescriptionReadOnly from '~/components/dashboard/prescription/ChewyPrescriptionReadOnly'
import PrescriptionActions from '~/components/dashboard/prescription/prescription-editor/actions/PrescriptionActions'
import usePrescriptionEditor, {
  type PrescriptionEditorProps,
} from '~/components/dashboard/prescription/prescription-editor/hooks/usePrescriptionEditor'
import PrescriptionBody from '~/components/dashboard/prescription/prescription-editor/PrescriptionBody'
import PrescriptionHeader from '~/components/dashboard/prescription/prescription-editor/PrescriptionHeader'
import {
  PrescriptionActionContext,
  type PrescriptionActionContextType,
} from '~/components/dashboard/prescription/PrescriptionContext'
import DialogNames from '~/constants/DialogNames'
import type { CRUDOrderOptions } from '~/types'

const useStyles = makeStyles(
  (theme) => ({
    buttonContainer: {
      boxShadow: '0 -1px 3px 0 rgba(0,0,0,0.1)',
      backgroundColor: theme.colors.tableBackground,
    },
  }),
  { name: 'BasePrescription' },
)

const BasePrescription = (props: PrescriptionEditorProps) => {
  const classes = useStyles()

  const [ignoreUnsavedChanges, setIgnoreUnsavedChanges] = useState(false)

  const {
    isChewyReadOnly,
    isChewyReactiveRxReadOnly,
    ...prescriptionBodyProps
  } = usePrescriptionEditor(props)

  const {
    disableDrafts,
    activeWorkflow,
    clientId,
    currentDoctorHasDvmLicense,
    currentDoctorHasMatchingStateLicense,
    fields: { automaticallyCreateTask },
    handleSave,
    hasUnsavedData,
    isChewyCatalogItemNotLinked,
    isCompoundingReasonRequired,
    isDvmLicenseRequired,
    isEdit,
    isTemplatesLoaded,
    outsideSoap,
    patientId,
    prescription,
    taskId,
    isChewyActiveRxReadOnly,
    isRetailOrderLineItem,
  } = prescriptionBodyProps

  const beforeSave = useCallback(
    (options?: CRUDOrderOptions) => {
      setIgnoreUnsavedChanges(true)
      handleSave({
        ...options,
        afterSaveCallback: () => setIgnoreUnsavedChanges(false),
      })
    },
    [handleSave],
  )

  const actionContext = useMemo<PrescriptionActionContextType>(
    () => ({
      disableDrafts,
      automaticallyCreateTaskField: automaticallyCreateTask,
      isChewyCatalogItemNotLinked,
      isCompounded: Boolean(isCompoundingReasonRequired),
      isDvmLicenseRequired,
      isEdit,
      isRetailOrderLineItem,
      prescriptionForTracking: {
        id: prescription.id,
        businessId: prescription.businessId,
      },
      prescriptionSignerId: prescription.signerId || prescription.signer?.id,
      onApprove: () => beforeSave({ isApprove: true }),
      onDecline: (declineReason: string) => beforeSave({ declineReason }),
      onEmail: () => beforeSave({ email: true }),
      onPrint: () => beforeSave({ print: true }),
      onSave: beforeSave,
      onSaveDraft: () => beforeSave({ isSaveDraft: true }),
      patientId,
      stateId: prescription.stateId,
      isChewyActiveRxReadOnly,
    }),
    [
      disableDrafts,
      automaticallyCreateTask,
      isChewyCatalogItemNotLinked,
      isCompoundingReasonRequired,
      isDvmLicenseRequired,
      isEdit,
      prescription,
      beforeSave,
      isChewyActiveRxReadOnly,
    ],
  )

  if (isChewyReadOnly) {
    return (
      <ChewyPrescriptionReadOnly
        clientId={clientId}
        isChewyReactiveRxReadOnly={isChewyReactiveRxReadOnly}
        patientId={patientId}
        prescription={prescription}
        px={2}
      />
    )
  }

  return (
    <>
      <Grid px={2}>
        <PrescriptionHeader
          clientId={clientId}
          patientId={patientId}
          prescription={prescription}
          taskId={taskId}
        />
      </Grid>
      <PrescriptionBody {...prescriptionBodyProps} />
      {isTemplatesLoaded ? (
        <Grid className={classes.buttonContainer} px={2} py={3}>
          <PrescriptionActionContext.Provider value={actionContext}>
            <PrescriptionActions
              currentDoctorHasDvmLicense={currentDoctorHasDvmLicense}
              currentDoctorHasMatchingStateLicense={
                currentDoctorHasMatchingStateLicense
              }
              outsideSoap={outsideSoap}
              workflow={activeWorkflow}
            />
          </PrescriptionActionContext.Provider>
        </Grid>
      ) : null}
      <LeaveConfirmationDialog
        navigateOnCancel
        dialogName={DialogNames.EXPANDER_CONFIRM_CLOSE}
        hasUnsavedData={ignoreUnsavedChanges ? false : hasUnsavedData}
        onProceed={() => handleSave({ isSaveDraft: true })}
      />
    </>
  )
}

export default BasePrescription
